import React from "react";
import { Box, TextField, Switch, FormControlLabel } from "@material-ui/core";
import { useFormik } from "formik";
import * as yup from "yup";
import Button from "components/CustomButtons/Button.js";
import SubscriptionService from "services/SubscriptionService";
import { useSnackbar } from "hooks/useSnackbar";

const validationSchema = yup.object({
    name: yup.string().required("Subscription name is required"),
    planDescription: yup.string().required("Plan description is required"),
    setupFee: yup
        .number()
        .typeError("Setup fee must be a number")
        .required("Setup fee is required"),
    monthlyFee: yup
        .number()
        .typeError("Monthly fee must be a number")
        .required("Monthly fee is required"),
    cancelFee: yup.number().nullable().typeError("Cancel fee must be a number"), // <-- solo validación de tipo
});

export default function SubscriptionForm({ initialValues = {}, onSuccess = () => {} }) {
    const openSnackbar = useSnackbar();

    const formik = useFormik({
        initialValues: {
            name: initialValues.name || "",
            planDescription: initialValues.planDescription || "",
            setupFee: initialValues.setupFee || "",
            monthlyFee: initialValues.monthlyFee || "",
            cancelFee: initialValues.cancelFee || "",
            active: initialValues.active ?? true,
        },
        validationSchema,
        enableReinitialize: true,
        onSubmit: async (values, { setSubmitting, resetForm }) => {
            try {
                const payload = { ...values };

                if (initialValues.id) {
                    await SubscriptionService.put(initialValues.id, payload);
                    openSnackbar("success", "Subscription updated successfully");
                } else {
                    await SubscriptionService.post(payload);
                    openSnackbar("success", "Subscription created successfully");
                }

                onSuccess();
                resetForm();
            } catch (error) {
                console.error(error);
                openSnackbar("error", "Error saving subscription");
            } finally {
                setSubmitting(false);
            }
        },
    });

    return (
        <Box component="form" onSubmit={formik.handleSubmit} display="flex" flexDirection="column">
            <Box mb={1}>
                <TextField
                    fullWidth
                    label="Subscription Name"
                    name="name"
                    value={formik.values.name}
                    onChange={formik.handleChange}
                    error={formik.touched.name && Boolean(formik.errors.name)}
                    helperText={formik.touched.name && formik.errors.name}
                />
            </Box>

            <Box mb={1}>
                <TextField
                    fullWidth
                    label="Plan Description"
                    name="planDescription"
                    value={formik.values.planDescription}
                    onChange={formik.handleChange}
                    error={formik.touched.planDescription && Boolean(formik.errors.planDescription)}
                    helperText={formik.touched.planDescription && formik.errors.planDescription}
                />
            </Box>

            <Box mb={1}>
                <TextField
                    fullWidth
                    label="Setup Fee ($)"
                    name="setupFee"
                    type="number"
                    value={formik.values.setupFee}
                    onChange={formik.handleChange}
                    error={formik.touched.setupFee && Boolean(formik.errors.setupFee)}
                    helperText={formik.touched.setupFee && formik.errors.setupFee}
                />
            </Box>

            <Box mb={1}>
                <TextField
                    fullWidth
                    label="Monthly Fee ($)"
                    name="monthlyFee"
                    type="number"
                    value={formik.values.monthlyFee}
                    onChange={formik.handleChange}
                    error={formik.touched.monthlyFee && Boolean(formik.errors.monthlyFee)}
                    helperText={formik.touched.monthlyFee && formik.errors.monthlyFee}
                />
            </Box>

            <Box mb={1}>
                <TextField
                    fullWidth
                    label="Cancel Fee ($)"
                    name="cancelFee"
                    type="number"
                    value={formik.values.cancelFee}
                    onChange={formik.handleChange}
                    error={formik.touched.cancelFee && Boolean(formik.errors.cancelFee)}
                    helperText={formik.touched.cancelFee && formik.errors.cancelFee}
                />
            </Box>

            <Box mb={1}>
                <FormControlLabel
                    control={
                        <Switch
                            checked={formik.values.active}
                            onChange={formik.handleChange}
                            name="active"
                            color="primary"
                        />
                    }
                    label="Subscription Active"
                />
            </Box>

            <Box mt={2} textAlign="center">
                <Button type="submit" color="rose" disabled={formik.isSubmitting}>
                    {initialValues.id ? "Update" : "Create"} Subscription
                </Button>
            </Box>
        </Box>
    );
}
